import { AccountClaimBanner } from "@/site/components/auth/account-claim";
import { authSurfaceFromNext, trackFunnel } from "@/lib/funnel-events";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/user-avatar";
import { useAuth } from "@/hooks/use-auth";
import { api, edenFetch } from "@/lib/eden";
import { AuthSocial } from "@/site/components/auth/auth-social";
import { oauthErrorMessage } from "@/site/components/auth/auth-utils";
import { ForgotPasswordForm } from "@/site/components/auth/forgot-password-form";
import { LoginForm } from "@/site/components/auth/login-form";
import { RegisterForm } from "@/site/components/auth/register-form";
import { SimpleFooter, SiteFooter } from "@/site/components/site-footer";
import { SiteHeader } from "@/site/components/site-header";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ChevronDown, ChevronUp, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";

export type AuthStage = "login" | "register" | "forgot";

interface DevUser {
  user_id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  is_admin: boolean;
}

/** Legacy `getMembershipGuardProps()` defaults for `location="auth-page"`. */
const COPY: Record<AuthStage, { title: string; subtitle: string }> = {
  login: { title: "Welcome!", subtitle: "Sign in to continue" },
  register: { title: "Become a member", subtitle: "Create your account" },
  forgot: {
    title: "Forgot Password?",
    subtitle: "Enter your email to reset your password",
  },
};

const STAGE_PATH: Record<AuthStage, "/login" | "/register" | "/forgot"> = {
  login: "/login",
  register: "/register",
  forgot: "/forgot",
};

/**
 * The auth wall — the page behind `/login`, `/register` and `/forgot`.
 *
 * This is a port of the legacy `pages/auth-page` → `components/auth/authwall`
 * pair: a serif title block, the three social buttons, the "OR use an email
 * address" rule, then the stage's form. `stage` is driven by the route rather
 * than by local state, so switching between sign-in and register changes the
 * URL (and keeps the query string, including `?next=`) exactly as it used to.
 *
 * Nothing here navigates on success. `__root.tsx` owns the post-auth redirect
 * for every path into the app — password login, OAuth return, a soft-login
 * link, or someone who was already signed in when they hit the page.
 */
export default function AuthPage({ stage = "login" }: { stage?: AuthStage }) {
  const navigate = useNavigate();
  const { devLogin } = useAuth();
  const [loggingInAs, setLoggingInAs] = useState<string | null>(null);
  const [showDevPicker, setShowDevPicker] = useState(false);
  const [devError, setDevError] = useState("");
  // What the member typed on the login form before pressing "Forgot password?" (BDAY-474).
  const [forgotEmail, setForgotEmail] = useState("");

  // OAuth callbacks bounce failures back here as `?error=<code>`.
  const oauthError = oauthErrorMessage(
    new URLSearchParams(window.location.search).get("error") ?? undefined,
  );

  const goToStage = (stageNext: AuthStage) => {
    // Carry `?next=` across the switch (legacy `onAuthSwitch` preserved the query
    // string); the spent `?error=` code is dropped.
    const next = new URLSearchParams(window.location.search).get("next");
    void navigate({ to: STAGE_PATH[stageNext], search: next ? { next } : {} });
  };

  const isDevEnv = (import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE) === "development";
  const { data: users, isLoading: usersLoading } = useQuery<DevUser[]>({
    queryKey: ["/api/public/auth/users"],
    queryFn: async (): Promise<DevUser[]> =>
      (await edenFetch(api.api.public.auth.users.get())) as DevUser[],
    enabled: isDevEnv,
  });

  const handleDevLogin = async (user: DevUser) => {
    setLoggingInAs(user.user_id);
    try {
      await devLogin(user.user_id);
    } catch (err: unknown) {
      setDevError(err instanceof Error ? err.message : "Could not log in");
    } finally {
      setLoggingInAs(null);
    }
  };

  // PRD 13 K1: legacy's per-surface auth-wall beacons (/mycards/login, /card/read/register,
  // /dob…/register): the full-page wall, keyed by the page `?next=` came from.
  useEffect(() => {
    const next = new URLSearchParams(window.location.search).get("next");
    trackFunnel("authwall_shown", { surface: authSurfaceFromNext(next), stage });
  }, [stage]);

  const { title, subtitle } = COPY[stage];

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* SiteHeader routes /login, /register and /forgot to its `simpleChrome`
          branch — the tan bar with the logo and "Welcome Guest", same as legacy. */}
      <SiteHeader />

      <main id="main-content" className="min-h-[80vh]">
        <div className="mx-auto max-w-[600px] px-5 py-10">
          <div className="flex w-full flex-col items-center gap-4">
            <div className="text-center">
              <h1
                className="font-serif text-[1.75rem] leading-tight sm:text-[2.625rem] sm:leading-[2.8125rem]"
                data-testid="text-auth-heading"
              >
                {title}
              </h1>
              <h2 className="mt-1 text-base leading-6 font-normal">{subtitle}</h2>
            </div>

            {/* PRD 08 D21: an "I already have an account" / merge is waiting on this sign-in. */}
            {stage === "login" && <AccountClaimBanner />}
            {stage !== "forgot" && (
              <>
                <AuthSocial className="gap-3" />
                {/* Legacy `.split-title`: serif label with a hairline rule either side. */}
                <p className="flex w-full max-w-[450px] items-center gap-4 font-serif text-base before:h-px before:flex-1 before:bg-border before:content-[''] after:h-px after:flex-1 after:bg-border after:content-['']">
                  OR use an email address
                </p>
              </>
            )}

            {stage === "login" && (
              <LoginForm
                initialError={oauthError}
                onForgotClick={(typed) => {
                  setForgotEmail(typed);
                  goToStage("forgot");
                }}
                onSwitch={() => goToStage("register")}
              />
            )}
            {stage === "register" && (
              <RegisterForm initialError={oauthError} onSwitch={() => goToStage("login")} />
            )}
            {stage === "forgot" && (
              <ForgotPasswordForm
                defaultEmail={forgotEmail || undefined}
                onBack={() => goToStage("login")}
                notice={
                  new URLSearchParams(window.location.search).get("error") === "legacy_reset_link"
                    ? oauthError
                    : undefined
                }
              />
            )}

            {isDevEnv && users && users.length > 0 && (
              <div className="w-full max-w-[450px]">
                <button
                  type="button"
                  className="flex w-full items-center justify-center gap-2 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
                  aria-expanded={showDevPicker}
                  aria-controls="dev-picker-panel"
                  onClick={() => setShowDevPicker(!showDevPicker)}
                  data-testid="button-toggle-dev-login"
                >
                  <span>Developer Login</span>
                  {showDevPicker ? (
                    <ChevronUp className="h-3 w-3" aria-hidden />
                  ) : (
                    <ChevronDown className="h-3 w-3" aria-hidden />
                  )}
                </button>

                {showDevPicker && (
                  <div id="dev-picker-panel" className="mt-2 rounded-md border p-2">
                    {usersLoading ? (
                      <div className="space-y-2">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {users?.map((user) => (
                          <Button
                            key={user.user_id}
                            variant="ghost"
                            className="h-auto w-full justify-start gap-3 py-2"
                            disabled={loggingInAs !== null}
                            onClick={() => handleDevLogin(user)}
                            data-testid={`button-login-${user.username}`}
                          >
                            <UserAvatar
                              avatarUrl={user.avatar_url}
                              name={user.display_name || user.username}
                              className="h-8 w-8"
                            />
                            <div className="min-w-0 text-left">
                              <span className="flex items-center gap-1.5 truncate text-base font-medium">
                                {user.display_name || user.username}
                                {user.is_admin && (
                                  <Badge
                                    variant="secondary"
                                    className="gap-0.5 px-1.5 py-0 text-xs"
                                    data-testid={`badge-admin-${user.username}`}
                                  >
                                    <ShieldCheck className="h-3 w-3" />
                                    admin
                                  </Badge>
                                )}
                              </span>
                              <p className="text-sm text-muted-foreground">@{user.username}</p>
                            </div>
                            {loggingInAs === user.user_id && (
                              <span className="ml-auto text-sm text-muted-foreground">
                                Signing in...
                              </span>
                            )}
                          </Button>
                        ))}
                      </div>
                    )}
                    {devError && <p className="mt-2 text-sm text-destructive">{devError}</p>}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Legacy: login/register keep the full footer, /forgot gets the simple one. */}
      {stage === "forgot" ? <SimpleFooter /> : <SiteFooter />}
    </div>
  );
}
